import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../../services/configUrls";

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [supportUsers, setSupportUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acceptingTickets, setAcceptingTickets] = useState(new Set());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Get access token from localStorage
  const getAccessToken = () => {
    return localStorage.getItem("accessToken");
  };

  // ✅ FIX: Fetch unassigned tickets with pagination using query params
  const fetchUnassignedTickets = useCallback(
    async (page = 1) => {
      try {
        const accessToken = getAccessToken();

        if (!accessToken) {
          throw new Error("No access token found");
        }

        // ✅ Changed to use query parameters instead of request body
        const config = {
          method: "post",
          maxBodyLength: Infinity,
          url: `${BASE_URL}/unassigned-tickets?page=${page}&page_size=${perPage}`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          // ✅ Removed the data field - no body needed
        };

        const response = await axios.request(config);
        console.log("✅ Unassigned Tickets Data:", response.data);

        // Update state with the new API response structure
        setTickets(response.data.tickets || []);

        // Update pagination data with new field names
        setTotalPages(response.data.total_pages);
        setTotalItems(response.data.unassigned_count);
        setPerPage(response.data.page_size);

        setError(null);
      } catch (error) {
        console.error("❌ Error fetching unassigned tickets:", error);
        if (!tickets.length) {
          setError(error.message || "Failed to fetch tickets");
        }
      }
    },
    [perPage]
  ); // ✅ Removed tickets.length from dependencies

  // Fetch support user IDs from API
  useEffect(() => {
    const fetchSupportUsers = async () => {
      try {
        const accessToken = getAccessToken();

        if (!accessToken) {
          throw new Error("No access token found");
        }

        const config = {
          method: "get",
          maxBodyLength: Infinity,
          url: `${BASE_URL}/aiservices/supportUserids`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };

        const response = await axios.request(config);
        console.log("✅ Support Users Data:", response.data);
        setSupportUsers(response.data);
        setError(null);
      } catch (error) {
        console.error("❌ Error fetching support users:", error);
        setError(error.message || "Failed to fetch support users");
      } finally {
        setLoading(false);
      }
    };

    fetchSupportUsers();
  }, []);

  // Fetch tickets when currentPage changes
  useEffect(() => {
    if (supportUsers !== null) {
      setLoading(true);
      fetchUnassignedTickets(currentPage).finally(() => {
        setLoading(false);
      });
    }
  }, [supportUsers, currentPage, fetchUnassignedTickets]);

  // Auto-refresh tickets every 30 seconds (only for current page)
  useEffect(() => {
    if (supportUsers === null) return;

    const interval = setInterval(() => {
      fetchUnassignedTickets(currentPage);
    }, 30000);

    return () => clearInterval(interval);
  }, [supportUsers, currentPage, fetchUnassignedTickets]);

  const formatFullName = (firstName, middleName, lastName) => {
    const nameParts = [firstName, middleName, lastName].filter(
      (part) => part && part.trim()
    );
    return nameParts.length > 0 ? nameParts.join(" ") : "N/A";
  };

  // Check if current support user is already assigned to the ticket
  const isCurrentUserAssigned = (assignedUsers, currentUserId) => {
    if (!assignedUsers || !currentUserId) return false;
    const assignedIds = assignedUsers.split(",").map((id) => id.trim());
    return assignedIds.includes(currentUserId.toString());
  };

  // Handle accept ticket action
  const handleAcceptTicket = async (ticketId) => {
    try {
      const accessToken = getAccessToken();

      if (!accessToken) {
        throw new Error("No access token found");
      }

      setAcceptingTickets((prev) => new Set([...prev, ticketId]));

      const data = JSON.stringify({
        support_meetings_id: ticketId,
      });

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/supportAssignment`,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        data: data,
      };

      const response = await axios.request(config);
      console.log(
        "✅ Ticket accepted successfully:",
        JSON.stringify(response.data)
      );

      // Refresh current page after accepting
      await fetchUnassignedTickets(currentPage);
      alert("Ticket accepted successfully!");
    } catch (error) {
      console.error("❌ Error accepting ticket:", error);

      if (error.response) {
        alert(
          `Failed to accept ticket: ${
            error.response.data.message || "Server error"
          }`
        );
      } else if (error.request) {
        alert("Network error: Please check your connection");
      } else {
        alert(`Error: ${error.message}`);
      }
    } finally {
      setAcceptingTickets((prev) => {
        const newSet = new Set(prev);
        newSet.delete(ticketId);
        return newSet;
      });
    }
  };

  // Pagination handler that properly updates state
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Truncate text helper
  const truncateText = (text, maxLength) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            Loading support data...
          </h3>
          <p className="text-xs text-slate-500">
            Please wait while we fetch the information
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl border border-red-200 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            Error loading data
          </h3>
          <p className="text-xs text-slate-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-3">
      <div className="max-w-full mx-auto">
        {/* Header Section - Compact */}
        <div className="mb-4">
          <h1 className="text-xl font-bold text-blue-600">Support Tickets</h1>
        </div>

        {tickets.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">
              No tickets available
            </h3>
            <p className="text-xs text-slate-500">
              New support tickets will appear here when submitted
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            {/* Table Header Stats - Compact */}
            <div className="bg-blue-600 px-3 py-2">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-white">
                  Active Tickets
                </h2>
                <div className="flex items-center space-x-2">
                  <span className="bg-white/20 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                    Page {currentPage} of {totalPages}
                  </span>
                  <span className="bg-white/20 text-white px-2 py-0.5 rounded-full text-xs font-medium">
                    {totalItems} Total
                  </span>
                </div>
              </div>
            </div>

            {/* Compact Table - No Horizontal Scroll */}
            <div className="w-full">
              <table className="w-full table-fixed">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="w-[8%] px-2 py-2 text-left text-xs font-semibold text-slate-700">
                      Status
                    </th>
                    <th className="w-[6%] px-2 py-2 text-left text-xs font-semibold text-slate-700">
                      ID
                    </th>
                    <th className="w-[15%] px-2 py-2 text-left text-xs font-semibold text-slate-700">
                      Student
                    </th>
                    <th className="w-[12%] px-2 py-2 text-left text-xs font-semibold text-slate-700">
                      State
                    </th>
                    <th className="w-[14%] px-2 py-2 text-left text-xs font-semibold text-slate-700">
                      Issue Type
                    </th>
                    <th className="w-[30%] px-2 py-2 text-left text-xs font-semibold text-slate-700">
                      Description
                    </th>
                    <th className="w-[15%] px-2 py-2 text-center text-xs font-semibold text-slate-700">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tickets.map((ticket) => {
                    const isAssigned = isCurrentUserAssigned(
                      ticket.assigned_users,
                      supportUsers
                    );
                    const isAccepting = acceptingTickets.has(
                      ticket.support_meetings_id
                    );

                    return (
                      <tr
                        key={ticket.support_meetings_id}
                        className={`transition-colors ${
                          isAssigned
                            ? "bg-red-50 hover:bg-red-100"
                            : "hover:bg-blue-50"
                        }`}
                      >
                        {/* Status Column */}
                        <td className="px-2 py-2">
                          {isAssigned ? (
                            <span className="inline-flex items-center text-xs text-red-600 font-medium">
                              <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                              Assigned
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs text-green-600 font-medium">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                              Available
                            </span>
                          )}
                        </td>

                        {/* ID Column */}
                        <td className="px-2 py-2">
                          <span
                            className={`inline-flex items-center justify-center w-10 h-6 rounded text-white text-xs font-bold ${
                              isAssigned ? "bg-red-500" : "bg-blue-500"
                            }`}
                          >
                            #{ticket.support_meetings_id}
                          </span>
                        </td>

                        {/* Student Column */}
                        <td className="px-2 py-2">
                          <div className="flex items-center space-x-1">
                            <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-semibold text-xs">
                                {formatFullName(
                                  ticket.first_name,
                                  ticket.middle_name,
                                  ticket.last_name
                                ).charAt(0)}
                              </span>
                            </div>
                            <p
                              className="font-medium text-xs text-slate-900 truncate"
                              title={formatFullName(
                                ticket.first_name,
                                ticket.middle_name,
                                ticket.last_name
                              )}
                            >
                              {truncateText(
                                formatFullName(
                                  ticket.first_name,
                                  ticket.middle_name,
                                  ticket.last_name
                                ),
                                20
                              )}
                            </p>
                          </div>
                        </td>

                        {/* State Column */}
                        <td className="px-2 py-2">
                          <span
                            className="text-slate-600 text-xs truncate block"
                            title={ticket.state_name || "N/A"}
                          >
                            {truncateText(ticket.state_name, 15)}
                          </span>
                        </td>

                        {/* Issue Type Column */}
                        <td className="px-2 py-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              isAssigned
                                ? "bg-red-100 text-red-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                            title={ticket.category_name}
                          >
                            {truncateText(ticket.category_name, 18)}
                          </span>
                        </td>

                        {/* Description Column */}
                        <td className="px-2 py-2">
                          <p
                            className="text-slate-600 text-xs leading-relaxed"
                            title={ticket.reason || "No reason provided"}
                          >
                            {truncateText(ticket.reason, 80)}
                          </p>
                        </td>

                        {/* Actions Column */}
                        <td className="px-2 py-2 text-center">
                          {isAssigned ? (
                            <button
                              disabled
                              className="px-2 py-1 bg-slate-300 text-slate-600 text-xs font-medium rounded cursor-not-allowed"
                            >
                              Assigned
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                handleAcceptTicket(ticket.support_meetings_id)
                              }
                              disabled={isAccepting}
                              className={`px-3 py-1 text-xs font-medium rounded shadow transition-all ${
                                isAccepting
                                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                                  : "bg-blue-600 text-white hover:bg-blue-700"
                              }`}
                            >
                              {isAccepting ? "Accepting..." : "Accept"}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="bg-slate-50 px-3 py-3 border-t border-slate-200">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-600">
                  Showing{" "}
                  <span className="font-semibold">
                    {tickets.length > 0 ? (currentPage - 1) * perPage + 1 : 0}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold">
                    {Math.min(
                      (currentPage - 1) * perPage + tickets.length,
                      totalItems
                    )}
                  </span>{" "}
                  of <span className="font-semibold">{totalItems}</span> tickets
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-2 py-1 text-xs rounded ${
                      currentPage === 1
                        ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                        : "bg-white text-slate-700 hover:bg-blue-50 border border-slate-300"
                    }`}
                  >
                    Previous
                  </button>

                  {getPaginationNumbers().map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`dots-${index}`}
                        className="px-2 text-slate-400"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-2 py-1 text-xs rounded ${
                          currentPage === page
                            ? "bg-blue-600 text-white font-semibold"
                            : "bg-white text-slate-700 hover:bg-blue-50 border border-slate-300"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-2 py-1 text-xs rounded ${
                      currentPage === totalPages
                        ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                        : "bg-white text-slate-700 hover:bg-blue-50 border border-slate-300"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Tickets;
